import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Heart, ThumbsUp, Star, MessageCircle, ShieldCheck, Users, PenSquare, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import nursingImg from "@/assets/collage-nursing.jpg";
import pharmacyImg from "@/assets/collage-pharmacy.jpg";
import medicalImg from "@/assets/collage-medical.jpg";
import internImg from "@/assets/collage-intern.jpg";
import ptImg from "@/assets/collage-pt.jpg";
import RatingForm from "@/components/RatingForm";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { getReviewTypeConfig, type ReviewType } from "@/lib/mockData";

const Stars = ({ n = 5 }: { n?: number }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: n }).map((_, i) => (
      <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
    ))}
  </div>
);

const ReviewChip = ({
  title,
  rating = 5,
  quote,
  author,
  ago,
  className = "",
}: {
  title: string;
  rating?: number;
  quote: string;
  author: string;
  ago: string;
  className?: string;
}) => (
  <div className={`absolute z-20 w-[160px] rounded-lg bg-white shadow-xl ring-1 ring-black/5 p-2 ${className}`}>
    <p className="text-[11px] font-semibold text-foreground leading-tight">{title}</p>
    <div className="mt-0.5"><Stars n={rating} /></div>
    <p className="mt-1 text-[10px] leading-snug text-foreground/80 line-clamp-2">"{quote}"</p>
    <div className="mt-1.5 flex items-center gap-1 pt-1.5 border-t border-border/60">
      <div className="w-4 h-4 rounded-full bg-gradient-to-br from-primary to-accent" />
      <span className="text-[10px] font-medium text-foreground">{author}</span>
      <span className="text-[9px] text-muted-foreground ml-auto">{ago}</span>
    </div>
  </div>
);

const FloatingIcon = ({
  icon: Icon,
  className = "",
  color = "text-accent",
}: {
  icon: typeof Heart;
  className?: string;
  color?: string;
}) => (
  <div className={`absolute z-30 w-10 h-10 rounded-full bg-white shadow-lg ring-1 ring-black/5 flex items-center justify-center ${className}`}>
    <Icon className={`w-5 h-5 ${color}`} />
  </div>
);

const features = [
  { icon: MessageCircle, title: "Real Reviews", desc: "From students like you", color: "bg-primary/10 text-primary" },
  
  { icon: ShieldCheck, title: "All Fields", desc: "Rotations, residencies, fellowships & internships", color: "bg-emerald-100 text-emerald-700" },
  { icon: Users, title: "Community", desc: "Learn, share, and grow", color: "bg-accent/10 text-accent" },
];

const Index = () => {
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleWriteReview = () => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "You must be signed in to write a review. Log in or create a free account to continue.",
        variant: "destructive",
        action: (
          <ToastAction altText="Go to login" onClick={() => navigate("/login")}>
            Sign in
          </ToastAction>
        ),
      });
      navigate("/login", { state: { from: "/" } });
      return;
    }
    setFormOpen(true);
  };

  const handleSubmitted = (submittedType: ReviewType) => {
    setFormOpen(false);
    navigate(getReviewTypeConfig(submittedType).path);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (search.trim()) navigate(`/search?q=${encodeURIComponent(search.trim())}`);
  };


  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-secondary/40 via-background to-accent/5">
        <div className="container py-12 lg:py-20 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          {/* LEFT — Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-border shadow-sm">
              <Heart className="w-4 h-4 text-accent fill-accent/20" />
              <span className="text-sm font-medium text-foreground">Real students. Real experiences. Real impact.</span>
              <Sparkles className="w-3.5 h-3.5 text-accent ml-1" />
            </div>

            <h1 className="mt-6 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-foreground">
              Read reviews.<br />
              Share your experience.<br />
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-primary via-primary to-accent bg-clip-text text-transparent">
                  Help others decide<span className="text-accent">.</span>
                </span>
                <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 300 10" preserveAspectRatio="none">
                  <path d="M2 7 Q 150 1 298 6" stroke="hsl(var(--accent))" strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.6" />
                </svg>
              </span>
            </h1>

            <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-lg leading-relaxed">
              Join a community of students and professionals sharing honest reviews about rotations, residencies, fellowships and internships across every field.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/opportunities">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-6 h-12 font-semibold shadow-md">
                  Find Opportunities
                </Button>
              </Link>
              <Button
                size="lg"
                variant="outline"
                onClick={handleWriteReview}
                className="rounded-lg px-6 h-12 font-semibold border-border bg-white gap-2"
              >
                <PenSquare className="w-4 h-4" />
                Write a Review
              </Button>

            </div>

            <form onSubmit={handleSearch} className="mt-6 max-w-xl">
              <div className="relative flex items-center bg-white rounded-xl shadow-md border border-border overflow-hidden">
                <Search className="absolute left-4 w-5 h-5 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search rotations, residencies, fellowships, companies…"
                  className="pl-12 pr-4 h-13 py-3 text-sm border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-foreground"
                />
                <Button type="submit" className="h-10 px-5 mr-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                  Search
                </Button>
              </div>
            </form>

            {/* Feature pills */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {features.map((f) => (
                <div key={f.title} className="flex items-start gap-2.5">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${f.color}`}>
                    <f.icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground leading-tight">{f.title}</p>
                    <p className="text-xs text-muted-foreground leading-snug mt-0.5">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* RIGHT — Collage with floating chips */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="relative hidden md:block h-[640px] lg:h-[700px]"
          >
            {/* Image collage — 5 tiles arranged in a loose 2×3 layout */}
            <img
              src={nursingImg}
              alt="Nursing student"
              className="absolute top-0 left-0 w-[48%] h-[42%] object-cover rounded-2xl shadow-xl rotate-[-2deg]"
            />
            <img
              src={pharmacyImg}
              alt="Pharmacy student"
              className="absolute top-[3%] right-0 w-[44%] h-[36%] object-cover rounded-2xl shadow-xl rotate-[2deg]"
            />
            <img
              src={medicalImg}
              alt="Medical students"
              className="absolute top-[44%] left-0 w-[44%] h-[34%] object-cover rounded-2xl shadow-xl rotate-[1deg]"
            />
            <img
              src={internImg}
              alt="Marketing intern"
              className="absolute top-[41%] right-0 w-[42%] h-[32%] object-cover rounded-2xl shadow-xl rotate-[-1deg]"
            />
            <img
              src={ptImg}
              alt="Physical therapy"
              className="absolute bottom-0 left-[22%] w-[50%] h-[30%] object-cover rounded-2xl shadow-xl rotate-[-2deg]"
            />

            {/* Center circular badge — placed in the gap between image rows */}
            <div className="absolute z-30 top-[39%] left-[44%] w-[115px] h-[115px] rounded-full bg-white shadow-2xl ring-1 ring-black/5 flex items-center justify-center text-center px-3">
              <p className="text-[10.5px] font-medium text-foreground leading-snug">
                Your experience can shape someone else's <span className="italic font-semibold text-accent">journey.</span>
              </p>
            </div>

            {/* Floating review chips — anchored to outer column edges to avoid covering faces */}
            <ReviewChip
              title="Nursing Rotation"
              quote="Amazing learning experience! The team was so supportive and I felt valued."
              author="Sarah M."
              ago="2 days ago"
              className="top-[30%] -left-[4%]"
            />
            <ReviewChip
              title="Pharmacy Rotation"
              quote="Great exposure to patient care and real-world pharmacy operations."
              author="James L."
              ago="1 week ago"
              className="top-[2%] -right-[4%]"
            />
            <ReviewChip
              title="PT Rotation"
              rating={4}
              quote="Incredible experience that confirmed my passion for PT!"
              author="David K."
              ago="3 days ago"
              className="bottom-[2%] -left-[4%]"
            />
            <ReviewChip
              title="Marketing Internship"
              quote="Hands-on projects, great mentors, and real responsibilities."
              author="Maya T."
              ago="5 days ago"
              className="bottom-[28%] -right-[4%]"
            />

            {/* Floating action icons — placed in image corners away from faces */}
            <FloatingIcon icon={Heart} color="text-accent fill-accent/30" className="top-[1%] left-[44%]" />
            <FloatingIcon icon={ThumbsUp} color="text-primary" className="top-[70%] right-[44%]" />
            <FloatingIcon icon={Star} color="text-emerald-600 fill-emerald-100" className="top-[40%] left-[18%]" />
            <FloatingIcon icon={MessageCircle} color="text-primary" className="bottom-[1%] right-[20%]" />
          </motion.div>
        </div>
      </section>

      {/* Trust band */}
      <section className="container pb-16 -mt-2">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="rounded-2xl bg-secondary/40 border border-border p-6 md:p-8 flex flex-col md:flex-row items-center gap-6 md:gap-10"
        >
          <div className="flex items-center gap-4 md:flex-1">
            <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-serif text-lg text-foreground">A community built on trust</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Share honestly. Review openly. Help each other make confident decisions about your future.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-6 md:gap-10 md:border-l md:border-border md:pl-10">
            {[
              { v: "Real", l: "Student Reviews" },
              { v: "All", l: "Rotations, Residencies, Fellowships & Internships" },
              { v: "100+", l: "Fields Represented" },
            ].map((s) => (
              <div key={s.l} className="text-center md:text-left">
                <p className="font-serif text-2xl md:text-3xl text-primary">{s.v}</p>
                <p className="text-xs text-muted-foreground mt-1">{s.l}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>
      <RatingForm
        type="rotation"
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmitted={handleSubmitted}
      />
      <Footer />
    </div>
  );
};

export default Index;
